import { Extension } from '@tiptap/core';
import { EditorState, NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const pluginKey = new PluginKey('atomSelectionHighlight');

/**
 * Adds an `in-selection` CSS class (via node decoration) to all atom nodes
 * that fall within a non-collapsed text selection range.
 *
 * Atom nodes use `user-select:none` / `contenteditable="false"`, so the
 * browser's native `::selection` highlight doesn't render on them.
 * This extension bridges the gap by applying a decoration that CSS can target.
 *
 * When the selection is a NodeSelection (user clicked the atom directly),
 * the targeted node is NOT decorated - its own visual feedback (resize
 * handles, outline, etc.) takes precedence.
 *
 * Uses plugin state so decorations are only recomputed when the doc or
 * selection changes - meta-only transactions (e.g. focus) return as-is.
 */

const buildDecorations = (state: EditorState): DecorationSet => {
  const { from, to } = state.selection;
  if (from === to) return DecorationSet.empty;

  const isNodeSel = state.selection instanceof NodeSelection;

  const decorations: Decoration[] = [];
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isAtom) return;
    if (isNodeSel && pos === from) return;
    decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: 'in-selection' }));
  });

  return decorations.length ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty;
};

const AtomSelectionHighlight = Extension.create({
  name: 'atomSelectionHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        state: {
          init(_, state) {
            return buildDecorations(state);
          },
          apply(tr, oldDecos, _oldState, newState) {
            // Skip recomputation for meta-only transactions (e.g. addToHistory, focus changes).
            if (!tr.selectionSet && !tr.docChanged) return oldDecos;
            return buildDecorations(newState);
          },
        },
        props: {
          decorations(state) {
            return pluginKey.getState(state);
          },
        },
      }),
    ];
  },
});

export default AtomSelectionHighlight;
